import { v4 as randomUUID } from "uuid";
import {
  CheckoutConfigInputError,
  CheckoutPostMessageKind,
  createHandshakeGate,
  type CheckoutConfig,
  type CheckoutReturn,
  type AdditionalFormSections,
} from "@highnoteplatform/types";

export type { AdditionalFormSections };

import { buildFrame } from "./buildFrame";
import { establishCheckoutChannel } from "./handshake";
import { type OnReady, createHandlePostMessage } from "./handlePostMessage";
import { validateInputs } from "./validateInputs";

export function renderCheckout(
  config: CheckoutConfig
): Promise<CheckoutReturn> {
  const errors = validateInputs(config);

  if (errors) {
    throw new CheckoutConfigInputError("Invalid checkout config", errors);
  }

  return new Promise((resolve) => {
    const { selector, onTokenizeError, onTokenizeSuccess } = config;

    const uuid = randomUUID();
    const frame = buildFrame({ uuid, checkoutConfig: config });

    const container = document.querySelector(selector) ?? document.body;

    // Dedupe the parent timeout and the iframe's relayed configure-timeout at
    // their convergence on onTokenizeError (see HandshakeGate). Single frame,
    // one credentialed channel.
    const gate = createHandshakeGate();

    // Attach the secure-channel load listener BEFORE inserting the frame so no
    // load event is missed. The client token is delivered to the iframe over
    // this private MessagePort, never the URL.
    const disposeSecureChannel = establishCheckoutChannel({
      frame,
      uuid,
      clientToken: config.clientToken,
      onError: gate.wrapFailure(onTokenizeError),
      onHandshakeAck: gate.ack,
    });

    container.append(frame);

    // eslint-disable-next-line prefer-const
    let handlePostMessage: ReturnType<typeof createHandlePostMessage>;

    const unmount = () => {
      // Tear down the handshake first so no pending timer can call onError
      // after the frame is gone.
      disposeSecureChannel();
      window.removeEventListener("message", handlePostMessage);
      container.replaceChildren("");
    };

    const onReady: OnReady = () => {
      resolve({ unmount });
    };

    handlePostMessage = createHandlePostMessage(
      uuid,
      gate.wrapRelay(onTokenizeError),
      onTokenizeSuccess,
      onReady
    );

    // remove explicit call after postMessage is implemented in iframe
    onReady({ kind: CheckoutPostMessageKind.CHECKOUT_READY });

    window.removeEventListener("message", handlePostMessage);
    window.addEventListener("message", handlePostMessage);
  });
}
